import React, { useEffect, useRef, useMemo, useCallback } from "react";
import { Table, Modal, Col, message } from "antd";
import styles from "./BuildQuote.module.scss";
import ConditionalRender from "../../../../reusable-components/ConditionalRender";
import InputField from "../../../../reusable-components/TNInputField";
import { InputfieldTypes } from "../../../../enums/InputFieldAttributesEnums";
import {
  QuoteFields,
  QuoteEditablesPlaceHolders,
  fieldNames,
} from "./constant";
import { useMaterials } from "../../../../context/materialListingContext";
import { useTruckDetailContext } from "../../../../context/TruckDetailsContext";
import {
  getColumns,
  headerQuoteTextAreaProps,
  quoteInputFields,
  SignatureTypeOptions,
} from "./constants";
import { useState } from "react";
import { useFormContext } from "react-hook-form";
import RadioComponent from "../../../../reusable-components/RadioComponent";
import TNButton from "../../../../reusable-components/TNButton";
import {
  ButtonType,
  ButtonShape,
} from "../../../../enums/ButtonAttributesEnums";
import SignatureCanvas from "react-signature-canvas";
import ProjectInformationSheetProofUpload from "../JobLead/ProjectInformationSheetProofUpload";
import { Storage } from "aws-amplify";
import { SignatureEnums } from "../../../../enums/SignatureEnums";
import { textToImageConvertor } from "../../../../utils/textToImageConvertor";
import clsx from "clsx";
import { useLocation } from "react-router-dom";
import { getCurrentDate } from "../../../../utils/dateUtils";
import moment from "moment-timezone";
import { disableFutureDates } from "../../../../utils/disabledDateUtil";
import { TIME_ZONE } from "../../../BidToolConfigurationView/constants";
import { DateFormat } from "../../../../enums/dateTimeFormat";
import { individualContent } from "../../../../Types/documentsTypes";
import TNLogo from "../../../../assets/images/TNLogoQuote.png";
import UploadSignature from "../../../../assets/images/upload-signature.svg";
import { formatPhoneNumber } from "../../../../utils/numberFormatterUtil";
import { useQuoteTableState } from "../../../../../src/hooks/BuildQuoteHooks/useQuoteTableState";
import { useQuoteSignature } from "../../../../../src/hooks/BuildQuoteHooks/useQuoteSignature";
import { useQuoteFormContent } from "../../../../../src/hooks/BuildQuoteHooks/useQuoteFormContent";

interface QuoteProps {
  isEdit: boolean;
  quoteTickedRows?: [];
  signatureState?: {
    url: string | null;
    type: string | null;
    textValue: string | null;
  };
  onSignatureUpdate?: (url: string, type?: string, textValue?: string) => void;
  setQuoteTickedRows?: () => {};
  companyName?: string;
  quoteFormContent?: individualContent;
  showEstimate?: string;
  salesPerson?: Object;
  quoteDetails?: Object;
}

const Quote = ({
  isEdit,
  quoteTickedRows,
  signatureState,
  onSignatureUpdate,
  setQuoteTickedRows,
  companyName,
  quoteFormContent,
  showEstimate,
  salesPerson,
  quoteDetails,
}: QuoteProps) => {
  const {
    email,
    phone,
    printname,
    title,
    date,
    signatureHeader,
    paymentHeader,
    signedFor,
  } = QuoteFields;
  const {
    signaturePlaceHolder,
    printNamePlaceHolder,
    titlePlaceHolder,
    phonePlaceHolder,
    emailPlaceHolder,
    datePlaceHolder,
    paymentPlaceHolder,
    siganturePlaceHolder,
  } = QuoteEditablesPlaceHolders;
  const { quoteDate } = quoteInputFields;

  const { getValues, watch, setValue } = useFormContext();
  moment.tz.setDefault(TIME_ZONE);

  const {
    dataSource,
    visiblePrices,
    handleRowSelectionChange,
    togglePriceVisibility,
  } = useQuoteTableState({
    quoteTickedRows,
    setQuoteTickedRows,
    showEstimate,
  });

  const {
    isSignUploadOpen,
    signatureType,
    signatureFile,
    isUploadModalOpen,
    tempSignatureText,
    tempCanvasData,
    uploadedSignatureFileList,
    sigCanvasRef,
    isSubmitting,
    handleSignUploadModalOpen,
    handleSignUploadModalClose,
    handleSignatureTypeChange,
    handleSignatureFileChange,
    handleOpenUploadModal,
    handleCloseUploadModal,
    handleSignUploadModalSubmit,
    setTempSignatureText,
    setTempCanvasData,
  } = useQuoteSignature({
    signatureState,
    onSignatureUpdate,
    setValue,
    getValues,
  });

  const { signatureDisplay, currentDate, handlePhoneChange } =
    useQuoteFormContent({
      signatureState,
      getValues,
      setValue,
    });

  // Memoized values
  const columns = useMemo(
    () =>
      getColumns({
        visiblePrices,
        togglePriceVisibility,
        handleSelectVendor: () => {},
      }),
    [visiblePrices, togglePriceVisibility],
  );

  const rowSelection = useMemo(
    () => ({
      onChange: handleRowSelectionChange,
      selectedRowKeys: quoteTickedRows?.map((row) => row._key),
    }),
    [handleRowSelectionChange, quoteTickedRows],
  );

  const handleSelectDatePicker = useCallback(
    (date: moment.Moment | null, dateString: string) => {
      setValue(fieldNames.QUOTE_DATE, dateString);
    },
    [setValue],
  );

  useEffect(() => {
    if (!!quoteDetails) {
      setValue("quotePrintName", quoteDetails?.quote_print_name);
      setValue("quoteEmail", quoteDetails?.quote_email);
      setValue("quotePhone", quoteDetails?.quote_phone);
      setValue("quoteTitle", quoteDetails?.quote_title);
    } else if (!!salesPerson) {
      setValue("quotePrintName", salesPerson?.name);
      setValue("quoteEmail", salesPerson?.email);
      setValue("quotePhone", salesPerson?.phone);
    }
  }, [salesPerson, quoteDetails]);

  // Initialize form content
  useEffect(() => {
    if (quoteFormContent) {
      setValue("quoteTextAreaIntro", quoteFormContent?.mainDescription);
      setValue("quotePaymentTerms", quoteFormContent?.paymentTerms);
      setValue("quoteSignature", quoteFormContent?.signatureTerms);
      setValue(fieldNames.TEMPLATE_COMPANY_NAME, quoteFormContent?.companyName);
      setValue(
        fieldNames.TEMPLATE_COMPANY_ADDRESS,
        quoteFormContent?.companyAddress,
      );
      setValue(
        fieldNames.TEMPLATE_COMPANY_CONTACT,
        quoteFormContent?.companyContact,
      );
      setValue(
        fieldNames.TEMPLATE_ADDRESS_HEADER,
        quoteFormContent?.quoteAddressTerms,
      );
      setValue(fieldNames.QUOTE_HEADER, quoteFormContent?.quoteHeader);
    }
  }, [quoteFormContent, setValue]);

  return (
    <div>
      <Modal
        okButtonProps={{
          title: "Upload",
          disabled: !signatureFile,
          onClick: () => {
            handleCloseUploadModal();
          },
        }}
        visible={isUploadModalOpen}
        onCancel={handleCloseUploadModal}
        width={"761px"}
        title="Upload Signature"
        className={styles.modalWrapper}
        destroyOnClose
        key={JSON.stringify(uploadedSignatureFileList?.[0])}
      >
        <ProjectInformationSheetProofUpload
          onFilesUploaded={handleSignatureFileChange}
          docType={"signature"}
          filesUploaded={uploadedSignatureFileList}
          key={"signature"}
          maxFileSize={5}
          allowedFileTypes={["image/png", "image/jpeg", "image/jpg"]}
        />
      </Modal>
      <Modal
        width={750}
        visible={isSignUploadOpen}
        onCancel={handleSignUploadModalClose}
        title="Signature"
        okText="Submit"
        confirmLoading={isSubmitting}
        onOk={handleSignUploadModalSubmit}
        className={styles.modalWrapper}
        okButtonProps={{
          loading: isSubmitting,
          disabled: isSubmitting,
        }}
        cancelButtonProps={{
          disabled: isSubmitting,
        }}
        maskClosable={!isSubmitting}
        keyboard={!isSubmitting}
      >
        <Col>
          <RadioComponent
            name="signatureType"
            options={SignatureTypeOptions}
            value={signatureType}
            onChange={handleSignatureTypeChange}
            disabled={isSubmitting}
          />
          <ConditionalRender
            visible={signatureType === SignatureEnums.MANUAL_SIGNATURE}
          >
            <div className={styles.signatureContainer}>
              <div className={styles.SignatureUploadContainerTextInput}>
                <InputField
                  type={InputfieldTypes.TEXT_AREA}
                  textAreaRows={1}
                  maxLength={25}
                  noResize={true}
                  autoSize
                  showCount={false}
                  name="_signatureText"
                  label="Signature Name"
                  placeholder="Enter Name"
                  value={tempSignatureText || watch("_signatureText")}
                  onChange={(e) => {
                    setTempSignatureText(e.target.value);
                    setValue("_signatureText", e.target.value);
                  }}
                  disabled={isSubmitting}
                />
              </div>
              <div className={styles.uploadButtonContainer}>
                <TNButton
                  type={ButtonType.PRIMARY}
                  text={
                    <div>
                      <img src={UploadSignature} /> Upload Signature
                    </div>
                  }
                  shape={ButtonShape.DEFAULT}
                  onClick={handleOpenUploadModal}
                  disabled={!!watch("_signatureText") || isSubmitting}
                  className={styles.uploadButton}
                />
              </div>
            </div>
          </ConditionalRender>
          <ConditionalRender
            visible={signatureType === SignatureEnums.DIGITAL_SIGNATURE}
          >
            <SignatureCanvas
              penColor="black"
              canvasProps={{
                width: 700,
                height: 200,
                className: styles.SignatureCanvas,
              }}
              ref={sigCanvasRef}
              disabled={isSubmitting}
            />
            <TNButton
              type={ButtonType.PRIMARY}
              text="Clear"
              shape={ButtonShape.DEFAULT}
              onClick={() => {
                sigCanvasRef.current?.clear();
                setTempCanvasData(null);
              }}
              disabled={isSubmitting}
            />
          </ConditionalRender>
        </Col>
      </Modal>

      <div className={styles.LogoAddressContainer}>
        <div>
          <img
            src={TNLogo}
            alt="Twisted Nails"
            className={styles.TwistedNailsLogo}
          />
        </div>
        <div
          className={clsx({
            [styles.AddressContainer]: isEdit,
            [styles.AddressContainer_Content]: !isEdit,
          })}
        >
          <ConditionalRender visible={isEdit}>
            {headerQuoteTextAreaProps.map((field) => (
              <InputField
                textAreaRows={field.textAreaRows}
                showCount
                maxLength={field.maxLength}
                type={field.type}
                name={field.name}
                className={styles.addressInputFields}
              />
            ))}
          </ConditionalRender>
          <ConditionalRender visible={!isEdit}>
            <h5>{getValues(fieldNames.TEMPLATE_COMPANY_NAME)}</h5>
            <h4>{getValues(fieldNames.TEMPLATE_COMPANY_ADDRESS)}</h4>
            <h4>{getValues(fieldNames.TEMPLATE_COMPANY_CONTACT)}</h4>
          </ConditionalRender>
        </div>
      </div>
      <div className={styles.SubHeadingContainer}>
        <ConditionalRender visible={isEdit}>
          <InputField
            textAreaRows={1}
            showCount
            maxLength={30}
            type={InputfieldTypes.TEXT_AREA}
            name={fieldNames.QUOTE_HEADER}
          />
        </ConditionalRender>
        <ConditionalRender visible={!isEdit}>
          <h1>{getValues(fieldNames.QUOTE_HEADER)}</h1>
        </ConditionalRender>
        <div className={styles.SubHeadingContent}>
          <ConditionalRender visible={isEdit}>
            <InputField
              textAreaRows={1}
              showCount
              maxLength={125}
              type={InputfieldTypes.TEXT_AREA}
              name={fieldNames.TEMPLATE_ADDRESS_HEADER}
            />
          </ConditionalRender>
          <ConditionalRender visible={!isEdit}>
            <div className={styles.AddressHeader}>
              {getValues(fieldNames.TEMPLATE_ADDRESS_HEADER)}
            </div>
          </ConditionalRender>
          <div className={styles.deliveryAddressBlock}>
            {getValues("deliveryAddress")}
            {`  (${getValues("deliveryAddressCoordinates")})`}
          </div>
        </div>
      </div>
      <div className={styles.TableContainer}>
        <Table
          columns={columns}
          dataSource={dataSource}
          pagination={false}
          rowSelection={rowSelection}
          rowKey={(record) => record._key}
        />
      </div>
      <div className={styles.TextContentContainer}>
        <ConditionalRender visible={isEdit}>
          <InputField
            textAreaRows={10}
            showCount
            maxLength={1000}
            type={InputfieldTypes.TEXT_AREA}
            name="quoteTextAreaIntro"
          />
        </ConditionalRender>
        <ConditionalRender visible={!isEdit}>
          <div className={styles.FirstTextContainer}>
            <p>{getValues("quoteTextAreaIntro")}</p>
          </div>
        </ConditionalRender>
        <ConditionalRender
          visible={!isEdit}
          fallback={
            <div className={styles.FirstTextContainerEdit}>
              <h3>{paymentHeader}</h3>
              <InputField
                textAreaRows={7}
                showCount
                maxLength={500}
                placeholder={paymentPlaceHolder}
                type={InputfieldTypes.TEXT_AREA}
                name="quotePaymentTerms"
              />
            </div>
          }
        >
          <div className={styles.FirstTextContainer}>
            <h3>{paymentHeader}</h3>
            <p>{getValues("quotePaymentTerms")}</p>
          </div>
        </ConditionalRender>
        <ConditionalRender
          visible={!isEdit}
          fallback={
            <div className={styles.FirstTextContainerEdit}>
              <h3>{signatureHeader}</h3>
              <InputField
                textAreaRows={7}
                showCount
                maxLength={500}
                placeholder={siganturePlaceHolder}
                type={InputfieldTypes.TEXT_AREA}
                name="quoteSignature"
              />
            </div>
          }
        >
          <div className={styles.FirstTextContainer}>
            <h3>{signatureHeader}</h3>
            <p>{getValues("quoteSignature")}</p>
          </div>
        </ConditionalRender>
      </div>
      <div className={styles.SignaturesContainer}>
        <div className={styles.LeftContainer}>
          <div className={styles.UnderLineEmpty}>
            <div className={styles.UnderLine}>
              {signatureDisplay.url ? (
                <img
                  src={signatureDisplay.url}
                  alt="signature"
                  className={styles.TNSignatureName}
                  onClick={() => isEdit && handleSignUploadModalOpen()}
                  style={{ cursor: isEdit ? "pointer" : "default" }}
                />
              ) : isEdit ? (
                <InputField
                  type={InputfieldTypes.TEXT}
                  name="signatur"
                  placeholder="Enter Signature *"
                  onClick={handleSignUploadModalOpen}
                  autoComplete="off"
                  readOnly
                  value=""
                />
              ) : null}
            </div>
            <h1 className={styles.signedForTN}>
              Signed for Twisted Nail Brokers Services, LLC.
            </h1>
          </div>
          <div className={clsx(styles.UnderLineEmpty, styles.InfoRow)}>
            <ConditionalRender
              visible={isEdit}
              fallback={
                <div className={styles.FullLine} style={{ top: "4.265rem" }}>
                  <span>{printname}</span>
                  <span className={styles.Printname}>
                    {getValues("quotePrintName") ?? ""}
                  </span>
                </div>
              }
            >
              <InputField
                type={InputfieldTypes.MARGINLESS_TEXT}
                name="quotePrintName"
                placeholder={printNamePlaceHolder}
                onChange={(e) => setValue("quotePrintName", e?.target?.value)}
                maxLength={80}
              />
              <h1>{printname}</h1>
            </ConditionalRender>
          </div>
          <div className={clsx(styles.UnderLineEmpty, styles.InfoRow)}>
            <ConditionalRender
              visible={isEdit}
              fallback={
                <div className={styles.FullLine} style={{ top: "6.69rem" }}>
                  <span>{title}</span>
                  {getValues("quoteTitle")}
                </div>
              }
            >
              <InputField
                type={InputfieldTypes.MARGINLESS_TEXT}
                name="quoteTitle"
                placeholder={titlePlaceHolder}
                onChange={(e) => setValue("quoteTitle", e?.target?.value)}
              />
              <h1>{title}</h1>
            </ConditionalRender>
          </div>
          <div className={clsx(styles.UnderLineEmpty, styles.InfoRow)}>
            <ConditionalRender
              visible={isEdit}
              fallback={
                <div className={styles.FullLine} style={{ top: "9.19rem" }}>
                  <span>{phone}</span>
                  {getValues("quotePhone")}
                </div>
              }
            >
              <InputField
                type={InputfieldTypes.MARGINLESS_TEXT}
                name="quotePhone"
                placeholder={phonePlaceHolder}
                onChange={handlePhoneChange}
              />
              <h1>{phone}</h1>
            </ConditionalRender>
          </div>
          <div className={clsx(styles.UnderLineEmpty, styles.InfoRow)}>
            <ConditionalRender
              visible={isEdit}
              fallback={
                <div className={styles.FullLine} style={{ top: "11.69rem" }}>
                  <span>{email}</span>
                  {getValues("quoteEmail")}
                </div>
              }
            >
              <InputField
                type={InputfieldTypes.MARGINLESS_TEXT}
                name="quoteEmail"
                placeholder="Enter Email *"
                onChange={(e) => setValue("quoteEmail", e?.target?.value)}
              />
              <h1>{email}</h1>
            </ConditionalRender>
          </div>
        </div>
        <div
          className={clsx({
            [styles.CenterContainer]: isEdit,
            [styles.CenterSaveContainer]: !isEdit,
          })}
        >
          <div className={clsx(styles.UnderLineEmpty, styles.InfoRow)}>
            <ConditionalRender
              visible={isEdit}
              fallback={
                <div className={styles.UnderLine}>
                  {getValues(quoteDate) ??
                    currentDate?.format(DateFormat.MM_DD_YYYY_SLASHED)}
                </div>
              }
            >
              <div className={styles.UnderLine}>
                <InputField
                  type={InputfieldTypes.DATE_PICKER}
                  name={fieldNames.QUOTE_DATE}
                  defaultDate={moment(getValues(quoteDate)) ?? currentDate}
                  placeholder={datePlaceHolder}
                  disabledDates={disableFutureDates}
                  onDateChange={handleSelectDatePicker}
                />
              </div>
            </ConditionalRender>
            <h1>{date}</h1>
          </div>
        </div>
        <div className={styles.RightContainer}>
          <div
            className={clsx(styles.UnderLineEmpty, {
              [styles.EditedUnderlineEmpty]: isEdit,
            })}
          >
            <div className={styles.UnderLine}></div>
            <h1 className={styles.CustomerSignedFor}>
              {signedFor}&nbsp;{companyName ?? ""}
            </h1>
          </div>
          <div className={styles.UnderLineEmpty}>
            <div className={styles.FullLine}>
              <h1>{printname}</h1>
            </div>
          </div>
          <div className={styles.UnderLineEmpty}>
            <div className={styles.FullLine}>
              <h1>{title}</h1>
            </div>
          </div>
          <div className={styles.UnderLineEmpty}>
            <div className={styles.FullLine}>
              <h1>{phone}</h1>
            </div>
          </div>
          <div className={styles.UnderLineEmpty}>
            <div className={styles.FullLine}>
              <h1>{email}</h1>
            </div>
          </div>
        </div>
        <div className={styles.CenterRightContainer}>
          <div
            className={clsx(styles.UnderLineEmpty, {
              [styles.EditedUnderlineEmpty]: isEdit,
            })}
          >
            <div className={styles.UnderLine}></div>
            <h1>{date}</h1>
          </div>
        </div>
      </div>
    </div>
  );
};

export default React.memo(Quote);
